import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { Table } from "@tanstack/react-table"
import { type ReactElement, useMemo } from "react"

import { modelIconMapping } from "../../../mappings/icons"
import { genericMemo } from "../../../utils/optimization"

interface ColumnVisibilityDropdownProps<TData> extends MenuVariantProps, MenuRootProps {
	children: ReactElement
	table: Table<TData>
}

export const ColumnVisibilityDropdown = genericMemo(function ColumnVisibilityDropdown<TData>(
	props: ColumnVisibilityDropdownProps<TData>,
) {
	const { children, table, size, variant, radius, accentColor, ...rest } = props

	const hideableColumns = useMemo(() => {
		return table.getAllColumns().filter((column) => column.getCanHide())
	}, [table])

	const isAllColumnsVisible = hideableColumns.every((column) => column.getIsVisible())

	const isSomeColumnsVisible = hideableColumns.some((column) => column.getIsVisible())

	const allColumnsVisibleChecked = isAllColumnsVisible ? true : isSomeColumnsVisible ? "indeterminate" : false

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} variant={variant} radius={radius} accentColor={accentColor}>
				<Menu.CheckboxItem
					checked={allColumnsVisibleChecked}
					onCheckedChange={table.toggleAllColumnsVisible}
					closeOnSelect={false}
				>
					Select all
					<Menu.CheckboxItemIndicator className="ml-[auto]">
						{(indeterminate) =>
							indeterminate ? (
								<LuIcon icon={modelIconMapping.indeterminate} />
							) : (
								<LuIcon icon={modelIconMapping.check} />
							)
						}
					</Menu.CheckboxItemIndicator>
				</Menu.CheckboxItem>
				<Menu.Separator />
				<Menu.Scroll>
					<Menu.Group>
						{hideableColumns.map((column) => {
							return (
								<Menu.CheckboxItem
									checked={column.getIsVisible()}
									onCheckedChange={column.toggleVisibility}
									key={column.id}
									closeOnSelect={false}
								>
									<span className="truncate">{column.columnDef.meta?.plainHeader}</span>
									<Menu.CheckboxItemIndicator className="ml-[auto]">
										<LuIcon icon={modelIconMapping.check} />
									</Menu.CheckboxItemIndicator>
								</Menu.CheckboxItem>
							)
						})}
					</Menu.Group>
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
